import _ from 'lodash'
import Wizard from 'scoreboard/components/wizard'
import DbConnection from './db-connection'
import SpreadsheetChooser from 'scoreboard/imports/spreadsheet-chooser'
import WorksheetOptions from 'scoreboard/imports/worksheet-options'
import NewColumnNameChooser from './new-column-name-chooser'
import DatasetsCreateFields from 'scoreboard/datasets/edit/datasets-create-fields'
import DataSourceTypeChooser from 'scoreboard/components/import-data-source/data-source-type-chooser'
import RestRequestForm from './rest/rest-request-form'
import RestDataSelector from './rest/rest-data-selector'
import { isSpreadsheet, isDatabase, isRest, hasNoDataSource }
	from 'scoreboard/imports/model/importable'

// views when using a database for the data source
const dbViews = [
	DataSourceTypeChooser,
	DbConnection
]

// views when using a spreadsheet for the data source
const spreadsheetViews = [
	DataSourceTypeChooser,
	SpreadsheetChooser,
	WorksheetOptions
]

// view when editing the datasource for a blank dataset
const blankViews = [ DataSourceTypeChooser ]

const restViews = [
	DataSourceTypeChooser,
	RestRequestForm,
	RestDataSelector
]

const DataSourceInfoWizard = Wizard.extend({

	/**
	 * Wizard for editing the data source info for an import
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Import|Dataset} options.model
	 *
	 * @param {Array} options.connections array of connections the user either
	 * has access to or can view because they are used by one of the imports the
	 * user has access to
	 *
	 * @param {Number} options.newColumnChooserIndex displays a column name chooser
	 * 					after a new spreadsheet is selected or after a new sheet is selected
	 * 					if it introduces different columns from the original. This value is
	 * 				    the index to display the chooser after
	 */
	initialize: function(options = {}) {
		this.options = options
		// #49724 store this on the wizard object, because it's value will change once we upload
		// a spreadsheet or specify a DB
		// #49904 this.model is null for scorecard/initiative imports, though,
		// so be careful
		this.startedWithNoDataSource = _.invoke(this.model, 'hasNoDataSource')
		this.options.doneKey = this.options.doneKey || 'common.save'
		// so we can call the initialize method manually and have the model
		// populated correctly (needed for ViWizard.start)
		this.model = options.model
		this.selectedDataSourceType = this.model?.getDataSourceMetadata('source')
		// labels for wizard titles (includes vi-wizard.js)
		let labels = _.invoke(this.model, 'labelCodes')
		let opts = _.extend({
			views: this.getViews(),
			isShowingPreviewNext: true,
			labels
		}, options)
		Wizard.prototype.initialize.call(this, opts)
	},

	next: function() {
		this.setViews(this.getViews())
		Wizard.prototype.next.apply(this, arguments)
	},

	getViews: function() {
		let views
		if (isSpreadsheet(this.selectedDataSourceType)) {
			views = spreadsheetViews
		} else if (isDatabase(this.selectedDataSourceType)) {
			views = dbViews
		} else if (isRest(this.selectedDataSourceType)) {
			views = restViews
		} else if (hasNoDataSource(this.selectedDataSourceType)) {
			views = blankViews
		}
		// #44888 when editing the data source info for an existing dataset,
		// we are including the first step so that the UI is the same everywhere.
		// From Scott's writeup: "If there’s a big technical reason why we
		// can’t do this, we can just disable the options that you can’t
		// choose and explain why in a tooltip on click."
		if (this.model.section === 'datasets') {
			views = _.clone(views)
			if (!this.model.isNew()) {
				if (!_.isNil(this.newColumnChooserIndex)) {
					// #33702 the previous step encountered a missing column exception
					// so show the new column name chooser
					views.splice(this.newColumnChooserIndex,
						0, NewColumnNameChooser)
				}
			}
			if (this.startedWithNoDataSource) {
				views.push(DatasetsCreateFields)
			}
		}
		return views
	},

	/**
	 * Update the wizard's views as needed, based on the data source type
	 * selected in the DataSourceTypeChooser step
	 */
	changeViews: function(selectedDataSourceType) {
		let views
		let { groupType, type } = selectedDataSourceType || {}
		this.selectedDataSourceType = groupType
		if (groupType === 'spreadsheet') {
			views = spreadsheetViews
		} else if (groupType === 'database') {
			views = dbViews
		} else if (type === 'none') {
			views = blankViews
		}
		if (_.isNil(views)) return

		this.setViews(views)
		this.render()
	},

	/**
	 * Instruct the wizard to display the new column name chooser after the
	 * provided index. To not display this step, pass in null
	 *
	 * @param newColumnChooserIndex the index after which to insert the new column name
	 * 						   chooser step; null to not display it
	 */
	setNewColumnChooserIndex(newColumnChooserIndex) {
		this.newColumnChooserIndex = newColumnChooserIndex
		this.setViews(this.getViews())
		this.render(this.options)
	},

	setNewColumnChooserIndexLast() {
		this.setNewColumnChooserIndex(this.steps)
	},

	/**
	 * @param submitHandler performs the submit action on the 'new-column-name-chooser'
	 * screen instead of the default action
	 */
	setSubmitHandler(submitHandler) {
		this.submitHandler = submitHandler
	},

	finish: function(model) {
		this.model.unset('newColumnNames')
		this.trigger('done', model)
	}

})

export default DataSourceInfoWizard

DataSourceInfoWizard.__filename__ = 'imports/data-source-info-wizard.js'
